// sapi-admin/src/pages/AdminNotifications.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell, BellOff, CheckCircle, XCircle, Package, DollarSign,
  AlertTriangle, Zap, ShoppingBag, Users, Tag,
  Image as ImageIcon, Megaphone, RefreshCw, Trash2,
  Filter, Check, Store
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { useAdminNotifications } from '../hooks/useAdminNotifications';

// Map des icônes par type
const getIconForType = (type) => {
  switch (type) {
    case 'new_order':
    case 'order_created':
      return <ShoppingBag size={16} className="text-emerald-400" />;
    case 'pi_sell_request':
    case 'pi_sell_pending':
      return <DollarSign size={16} className="text-orange-400" />;
    case 'eneo_recharge_success':
      return <Zap size={16} className="text-cyan-400" />;
    case 'order_status_updated':
      return <Package size={16} className="text-purple-400" />;
    case 'low_stock':
      return <AlertTriangle size={16} className="text-red-400" />;
    case 'new_user':
      return <Users size={16} className="text-pink-400" />;
    case 'new_coupon':
      return <Tag size={16} className="text-emerald-400" />;
    case 'new_banner':
      return <ImageIcon size={16} className="text-green-400" />;
    case 'new_announcement':
      return <Megaphone size={16} className="text-blue-400" />;
    case 'store_registration_pending':
    case 'store_activated':
      return <Store size={16} className="text-violet-400" />;
    default:
      return <Bell size={16} className="text-amber-400" />;
  }
};

// Composant GlassPanel
const GlassPanel = ({ children, style = {} }) => (
  <div style={{ background: "rgba(17,24,39,.85)", border: "1px solid rgba(255,255,255,.06)", borderRadius: 16, backdropFilter: "blur(16px)", ...style }}>
    {children}
  </div>
);

const Btn = ({ children, onClick, variant = 'ghost', size = 'sm', disabled = false, style = {} }) => {
  const V = {
    ghost: { background: 'rgba(255,255,255,.06)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,.08)' },
    blue: { background: 'rgba(59,130,246,.15)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)' },
    red: { background: 'rgba(239,68,68,.15)', color: '#f87171', border: '1px solid rgba(239,68,68,.3)' },
    green: { background: 'rgba(16,185,129,.15)', color: '#34d399', border: '1px solid rgba(16,185,129,.3)' },
    amber: { background: 'rgba(245,158,11,.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,.3)' },
  };
  const v = V[variant] || V.ghost;
  const pad = size === 'xs' ? '3px 8px' : size === 'sm' ? '5px 12px' : '8px 18px';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...v, padding: pad, borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .45 : 1, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'opacity .15s', whiteSpace: 'nowrap', ...style }}
    >
      {children}
    </button>
  );
};

export default function AdminNotifications() {
  const [filterType, setFilterType] = useState('all');
  const [filterRead, setFilterRead] = useState('all');
  const [selectedNotif, setSelectedNotif] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // ✅ Source de vérité unique — partagée avec AdminNotificationBell et le client
  const {
    notifications,
    stats,
    loading,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    deleteReadNotifications,
  } = useAdminNotifications();

  const filteredNotifications = notifications.filter(n => {
    if (filterType !== 'all' && n.type !== filterType) return false;
    if (filterRead === 'read' && !n.read) return false;
    if (filterRead === 'unread' && n.read) return false;
    return true;
  });

  const formatDate = (date) => {
    if (!date) return '';
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    if (diffHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    }
    return format(date, 'MMM dd, yyyy HH:mm');
  };

  const typeOptions = [
    { value: 'all', label: 'All types' },
    { value: 'new_order', label: 'Orders' },
    { value: 'pi_sell_request', label: 'Pi Sell' },
    { value: 'pi_sell_pending', label: 'Pi Pending' },
    { value: 'eneo_recharge_success', label: 'ENEO Recharge' },
    { value: 'eneo_recharge_admin', label: 'ENEO Recharge (Admin)' },
    { value: 'low_stock', label: 'Low Stock' },
    { value: 'store_registration_pending', label: 'Store Registration' },
    { value: 'store_activated', label: 'Store Activated' },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ paddingBottom: 48 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.18em', color: 'rgba(59,130,246,.8)', marginBottom: 4 }}>Inbox</p>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 10 }}>
              <Bell size={20} color="#3b82f6" strokeWidth={2} /> Admin Notifications
            </h1>
            <p style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>Manage all system notifications and alerts</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {loading && (
              <Btn variant="ghost" disabled>
                <RefreshCw size={13} style={{ animation: 'spin .8s linear infinite' }} />
                Syncing...
              </Btn>
            )}
            {stats.unread > 0 && (
              <Btn variant="blue" onClick={markAllAsRead}>
                <Check size={13} /> Mark all read
              </Btn>
            )}
            {notifications.filter(n => n.read).length > 0 && (
              <Btn variant="amber" onClick={deleteReadNotifications}>
                <Trash2 size={13} /> Delete read
              </Btn>
            )}
            {notifications.length > 0 && (
              <Btn variant="red" onClick={deleteAllNotifications}>
                <Trash2 size={13} /> Delete all
              </Btn>
            )}
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 12 }}>
          <div style={{ background: 'rgba(17,24,39,.85)', border: '1px solid rgba(59,130,246,.2)', borderRadius: 14, padding: '14px 16px' }}>
            <p style={{ fontSize: 9, color: '#475569', marginBottom: 8 }}>Total Notifications</p>
            <p style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{stats.total}</p>
          </div>
          <div style={{ background: 'rgba(17,24,39,.85)', border: '1px solid rgba(245,158,11,.2)', borderRadius: 14, padding: '14px 16px' }}>
            <p style={{ fontSize: 9, color: '#475569', marginBottom: 8 }}>Unread</p>
            <p style={{ fontSize: 22, fontWeight: 700, color: '#f59e0b' }}>{stats.unread}</p>
          </div>
          <div style={{ background: 'rgba(17,24,39,.85)', border: '1px solid rgba(16,185,129,.2)', borderRadius: 14, padding: '14px 16px' }}>
            <p style={{ fontSize: 9, color: '#475569', marginBottom: 8 }}>Orders</p>
            <p style={{ fontSize: 22, fontWeight: 700, color: '#34d399' }}>{stats.byType.new_order || 0}</p>
          </div>
          <div style={{ background: 'rgba(17,24,39,.85)', border: '1px solid rgba(139,92,246,.2)', borderRadius: 14, padding: '14px 16px' }}>
            <p style={{ fontSize: 9, color: '#475569', marginBottom: 8 }}>Pi Sell</p>
            <p style={{ fontSize: 22, fontWeight: 700, color: '#a78bfa' }}>{(stats.byType.pi_sell_request || 0) + (stats.byType.pi_sell_pending || 0)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <GlassPanel style={{ marginBottom: 16 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Filter size={13} color="#3b82f6" />
              <span style={{ fontSize: 11, color: '#64748b' }}>Filter by:</span>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ background: '#111827', border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, padding: '5px 10px', color: '#e2e8f0', fontSize: 11 }}>
                {typeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              <select value={filterRead} onChange={e => setFilterRead(e.target.value)} style={{ background: '#111827', border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, padding: '5px 10px', color: '#e2e8f0', fontSize: 11 }}>
                <option value="all">All</option>
                <option value="unread">Unread only</option>
                <option value="read">Read only</option>
              </select>
            </div>
            <p style={{ fontSize: 10, color: '#334155' }}>
              Showing {filteredNotifications.length} of {notifications.length}
            </p>
          </div>
        </div>
      </GlassPanel>

      {/* Notifications List */}
      <GlassPanel>
        {loading && notifications.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div style={{ width: 40, height: 40, border: '3px solid rgba(59,130,246,.3)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin .8s linear infinite', margin: '0 auto 12px' }} />
            <p style={{ color: '#475569', fontSize: 12 }}>Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <BellOff size={40} style={{ margin: '0 auto 12px', opacity: .3, color: '#475569' }} />
            <p style={{ color: '#475569', fontSize: 13, fontWeight: 600 }}>No notifications found</p>
            <p style={{ color: '#334155', fontSize: 11, marginTop: 4 }}>Try changing the filters</p>
          </div>
        ) : (
          <div>
            {filteredNotifications.map((notif) => {
              const Icon = getIconForType(notif.type);
              const isUnread = !notif.read;
              return (
                <div
                  key={notif.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 16px',
                    borderBottom: '1px solid rgba(255,255,255,.04)',
                    background: isUnread ? 'rgba(59,130,246,.05)' : 'transparent',
                    transition: 'background .15s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = isUnread ? 'rgba(59,130,246,.1)' : 'rgba(255,255,255,.02)'}
                  onMouseLeave={e => e.currentTarget.style.background = isUnread ? 'rgba(59,130,246,.05)' : 'transparent'}
                >
                  <div
                    style={{ width: 36, height: 36, borderRadius: 10, background: isUnread ? 'rgba(59,130,246,.15)' : 'rgba(255,255,255,.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    onClick={() => { setSelectedNotif(notif); setShowModal(true); }}
                  >
                    {Icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => { setSelectedNotif(notif); setShowModal(true); }}>
                    <p style={{ fontSize: 13, fontWeight: isUnread ? 700 : 500, color: isUnread ? '#fff' : '#94a3b8' }}>{notif.title}</p>
                    <p style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{notif.body}</p>
                    <p style={{ fontSize: 9, color: '#334155', marginTop: 4 }}>{formatDate(notif.createdAt)}</p>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {isUnread && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6' }} />}
                    {isUnread && (
                      <button onClick={() => markAsRead(notif.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, color: '#475569' }} title="Mark as read">
                        <CheckCircle size={14} />
                      </button>
                    )}
                    <button onClick={() => deleteNotification(notif.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, color: '#475569' }} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}

            {hasMore && (
              <div style={{ padding: '12px 16px', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,.04)' }}>
                <Btn variant="ghost" size="xs" onClick={loadMore}>
                  Load more
                </Btn>
              </div>
            )}
          </div>
        )}
      </GlassPanel>

      {/* Modal Detail */}
      <AnimatePresence>
        {showModal && selectedNotif && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }}
            onClick={() => setShowModal(false)}>
            <motion.div initial={{ scale: .95 }} animate={{ scale: 1 }} exit={{ scale: .95 }}
              style={{ background: '#0d1526', border: '1px solid rgba(255,255,255,.1)', borderRadius: 20, maxWidth: 500, width: '100%' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {getIconForType(selectedNotif.type)}
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{selectedNotif.title}</h3>
                </div>
                <button onClick={() => setShowModal(false)} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: 5, cursor: 'pointer' }}>
                  <XCircle size={14} color="#94a3b8" />
                </button>
              </div>
              <div style={{ padding: 20 }}>
                <p style={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.5 }}>{selectedNotif.body}</p>
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.05)', display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#475569' }}>
                  <span>Type: {selectedNotif.type}</span>
                  <span>{format(selectedNotif.createdAt, 'EEEE, MMMM d, yyyy HH:mm:ss')}</span>
                </div>
                <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  {!selectedNotif.read && (
                    <Btn variant="blue" size="xs" onClick={() => { markAsRead(selectedNotif.id); setShowModal(false); }}>
                      <Check size={12} /> Mark as read
                    </Btn>
                  )}
                  <Btn variant="red" size="xs" onClick={() => { deleteNotification(selectedNotif.id); setShowModal(false); }}>
                    <Trash2 size={12} /> Delete
                  </Btn>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}